'use client';

import {
  InternalNetworkAttackPathScopeLists,
  InternalNetworkSignalSetupButton,
  toasterApi,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useInternalNetworkOwnedPrincipals } from '../../../../hooks/api/useInternalNetworkOverview';
import { useInternalNetworkCrownJewels } from '../../../../hooks/api/useInternalNetworkScenarios';
import {
  useDeleteInternalNetworkCrownJewel,
  useRevokeInternalNetworkOwnedPrincipal,
} from '../../../../hooks/api/useInternalNetworkScopeSettings';
import { useOpenWebAppPage } from '../../../../hooks/useOpenWebAppPage';
import { WEB_APP_PATHS } from '../../../../utils/webAppPaths';
import { useInternalNetworkSignalSetupAvailability } from './useInternalNetworkSignalSetupAvailability';

type InternalNetworkAttackPathScopePanelProps = {
  networkId: string;
  isReadOnly: boolean;
  isDiscoveryActive: boolean;
};

export function InternalNetworkAttackPathScopePanel({
  networkId,
  isReadOnly,
  isDiscoveryActive,
}: InternalNetworkAttackPathScopePanelProps) {
  const tSettings = useTranslations('internalNetwork.settings');
  const openWebAppPage = useOpenWebAppPage();
  const signalSetup = useInternalNetworkSignalSetupAvailability(networkId);
  const { ownedPrincipals, isLoading: isOwnedPrincipalsLoading } =
    useInternalNetworkOwnedPrincipals({ networkId });
  const { crownJewels, isLoading: isCrownJewelsLoading } =
    useInternalNetworkCrownJewels({ networkId });
  const revokeOwnedPrincipal =
    useRevokeInternalNetworkOwnedPrincipal(networkId);
  const deleteCrownJewel = useDeleteInternalNetworkCrownJewel(networkId);
  const notifyRemovalFailure = (error: Error) => {
    console.error(
      new Error('Failed to update internal network attack-path scope', {
        cause: error,
      }),
    );
    toasterApi.error({
      title: tSettings('saveError'),
      description: error.message,
    });
  };

  return (
    <InternalNetworkAttackPathScopeLists
      ownedPrincipals={ownedPrincipals}
      crownJewels={crownJewels}
      isOwnedPrincipalsLoading={isOwnedPrincipalsLoading}
      isCrownJewelsLoading={isCrownJewelsLoading}
      onRevokeOwnedPrincipal={(entityId) =>
        revokeOwnedPrincipal.mutate(entityId, {
          onError: notifyRemovalFailure,
        })
      }
      onDeleteCrownJewel={(crownJewelId) =>
        deleteCrownJewel.mutate(crownJewelId, {
          onError: notifyRemovalFailure,
        })
      }
      isRevokingOwnedPrincipal={revokeOwnedPrincipal.isPending}
      isDeletingCrownJewel={deleteCrownJewel.isPending}
      disabled={isReadOnly || isDiscoveryActive}
      actions={
        isReadOnly ? null : (
          <InternalNetworkSignalSetupButton
            isAvailable={signalSetup.isAvailable}
            isLoading={signalSetup.isLoading}
            label="manage"
            variant="outline"
            alwaysShow
            onOpen={() =>
              openWebAppPage(
                WEB_APP_PATHS.internalNetworkSignalSetup(networkId),
              )
            }
          />
        )
      }
    />
  );
}
